import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, MainFormErrorMessage } from '@/components/ui/form'
import { FormInput } from '@/components/ui/form/FormInput'
import { FormTextarea } from '@/components/ui/form/FormTextarea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useActiveBusiness } from '@/hooks/useCurrentActiveBusiness'
import { orpc } from '@/lib/orpc/client'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  aiArticleSchema,
  type AiArticleSchemaType,
} from '@repo/shared/schemas/new-article/ai-article'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, InfoIcon, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute('/_dashboard/write/ai')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const { activeBusiness } = useActiveBusiness()

  const form = useForm<AiArticleSchemaType>({
    resolver: zodResolver(aiArticleSchema),
    defaultValues: {
      topic: '',
      keyPoints: '',
      wordCount: 'automatic',
      useBusinessInfo: false,
    },
  })

  const generateAiArticleMutation = useMutation(
    orpc.articles.generateAiArticle.mutationOptions(),
  )

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingData, setPendingData] = useState<AiArticleSchemaType | null>(
    null,
  )

  const handleSubmit = async (data: AiArticleSchemaType) => {
    if (!activeBusiness) {
      toast.error('Please select a business first')
      return
    }

    try {
      const result = await generateAiArticleMutation.mutateAsync({
        aiArticleData: data,
      })

      toast.success('AI article generation started!')
      console.log('AI Article generation result:', result)

      // Navigate back to the articles list or show success message
      navigate({
        to: '/edit/$articleId',
        params: { articleId: String(result.articleId) },
      })
    } catch (error) {
      console.error('Error generating AI article:', error)
      toast.error('Failed to start AI article generation. Please try again.')
    }
  }

  const attemptGenerate = (data: AiArticleSchemaType) => {
    // Save the data and open confirmation dialog
    setPendingData(data)
    setConfirmOpen(true)
  }

  const confirmAndGenerate = async () => {
    if (!pendingData) return
    setConfirmOpen(false)
    try {
      await handleSubmit(pendingData)
    } finally {
      setPendingData(null)
    }
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/write"
          className="flex gap-2 items-center mb-6 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to options
        </Link>

        <div className="space-y-6">
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="p-3 rounded-full bg-primary/10">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold">AI Article Writer</h1>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Let AI help you create engaging articles. Provide a topic, style,
              and key points, and we'll generate a well-structured article for
              you.
            </p>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(attemptGenerate)}
              className="p-6 space-y-8 rounded-lg border bg-card"
            >
              <FormInput
                control={form.control}
                name="topic"
                label="Article Topic"
                placeholder="e.g., The Future of Renewable Energy"
              />

              <FormTextarea
                control={form.control}
                name="keyPoints"
                label="Key Points (optional)"
                placeholder="Enter any specific points you'd like the article to cover. The more you provide, the better the article will be."
                textareaProps={{ rows: 4 }}
              />

              <div className="space-y-2">
                <label htmlFor="wordCount" className="text-sm font-medium">
                  Target Word Count
                </label>
                <select
                  {...form.register('wordCount')}
                  id="wordCount"
                  className="p-3 w-full rounded-md border focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="automatic">Automatic</option>
                  <option value="500">500 words</option>
                  <option value="1000">1000 words</option>
                  <option value="1500">1500 words</option>
                  <option value="2000">2000+ words</option>
                </select>
                {form.formState.errors.wordCount && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.wordCount.message}
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <input
                  {...form.register('useBusinessInfo')}
                  type="checkbox"
                  id="useBusinessInfo"
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/20"
                />
                <label
                  htmlFor="useBusinessInfo"
                  className="flex items-center gap-2"
                >
                  <p>Use my business information to personalize this article</p>
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoIcon className="w-4 h-4" />
                    </TooltipTrigger>
                    <TooltipContent>
                      We'll use the information we gathered about your business
                      to personalize the article.
                    </TooltipContent>
                  </Tooltip>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={
                  form.formState.isSubmitting ||
                  generateAiArticleMutation.isPending
                }
              >
                <Sparkles className="mr-2 w-4 h-4" />
                {form.formState.isSubmitting ||
                generateAiArticleMutation.isPending
                  ? 'Generating...'
                  : 'Generate Article'}
              </Button>
              <MainFormErrorMessage />
            </form>
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm generation</DialogTitle>
                  <DialogDescription>
                    Generating this article will count towards your usage. Are
                    you sure you want to proceed?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmOpen(false)}
                    disabled={
                      form.formState.isSubmitting ||
                      generateAiArticleMutation.isPending
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmAndGenerate}
                    disabled={
                      form.formState.isSubmitting ||
                      generateAiArticleMutation.isPending
                    }
                  >
                    {form.formState.isSubmitting ||
                    generateAiArticleMutation.isPending
                      ? 'Generating...'
                      : 'Yes, generate'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Form>
        </div>
      </div>
    </div>
  )
}
